import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck, User as UserIcon, Loader2, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/contexts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

function PwField({ id, label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label} <span className="text-destructive">*</span></Label>
      <div className="relative mt-1">
        <Input id={id} type={show ? "text" : "password"} data-testid={testid}
          value={value} onChange={onChange} className="pr-10" />
        <button type="button" onClick={() => setShow(!show)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-neutral-800">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function AccountSettings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!current) missing.push("Current Password");
    if (!next) missing.push("New Password");
    if (!confirm) missing.push("Confirm New Password");
    if (missing.length) { toast.error(`Please fill in: ${missing.join(", ")}`); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New Password and Confirm New Password don't match"); return; }
    if (next === current) { toast.error("New password must be different from the current one"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — please sign in again with the new password");
      setTimeout(async () => { await logout(); nav("/login"); }, 900);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update password"); }
    finally { setSaving(false); }
  };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-bold">Account Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your profile and password.</p>
      </div>

      <div className="bg-white rounded-md border border-border p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center font-heading text-lg font-bold">
            {initials}
          </div>
          <div className="flex-1">
            <div className="font-heading text-xl font-semibold">{user?.name}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
          </div>
          <div>
            {user?.role === "manager"
              ? <Badge className="bg-primary text-white hover:bg-primary/90"><ShieldCheck className="w-3 h-3 mr-1" />Manager</Badge>
              : <Badge variant="secondary"><UserIcon className="w-3 h-3 mr-1" />Sales Staff</Badge>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-md border border-border p-6">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Change Password</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Choose a strong new password. You'll be signed out and asked to log back in with the new password.
        </p>
        <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
          <PwField id="current" label="Current Password" value={current}
            onChange={(e) => setCurrent(e.target.value)} testid="current-password" />
          <PwField id="new" label="New Password" value={next}
            onChange={(e) => setNext(e.target.value)} testid="new-password" />
          <PwField id="confirm" label="Confirm New Password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} testid="confirm-password" />
          <div className="text-xs text-muted-foreground">Minimum 6 characters. Use a mix of letters, numbers and symbols for best security.</div>
          <div className="pt-2">
            <Button type="submit" data-testid="change-password-submit" disabled={saving}
              className="bg-primary hover:bg-primary/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
            </Button>
          </div>
        </form>
      </div>

      {user?.role !== "manager" && (
        <div className="bg-secondary/50 border border-border rounded-md p-4 text-xs text-muted-foreground">
          <b className="text-neutral-800">Forgot your current password?</b> Ask your Manager to reset it for you from User Management. Your Manager can always reset any account's password without needing to know the old one.
        </div>
      )}
    </div>
  );
}
